import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useCommonStore } from '@/store/useCommonStore';
import { useMyIpCreateAction } from '@/hooks/myIp/useMyIpCreateAction';
import { useMyIpCreationOrigin } from '@/components/MyIpCreationOriginProvider';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';

interface CreationRequest {
  sourceKey: string;
  phase: 'login' | 'refresh' | 'waiting' | 'creating';
  uid?: string;
  scopeKey?: string;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: unknown) => void;
}

/** 登录桥可能早于 React 提交返回，直接读取真实帐号和空间确认刷新结果。 */
function getCurrentIdentity() {
  const {
    userInfo, clientOId, clientId, isOverseaServer,
  } = useOfficialStore.getState();
  return {
    uid: userInfo.uid,
    scopeKey: JSON.stringify([userInfo.uid, clientOId ?? 0, clientId, isOverseaServer]),
  };
}

/** 稳定宿主持有登录到创建的完整动作；私有列表的查询开关仍由会话单独控制。 */
export function useMyIpLoginCreationAction({ requireCanvas = false }: { requireCanvas?: boolean; } = {}) {
  const action = useMyIpCreateAction({ requireCanvas });
  const session = useMyIpSession();
  const origin = useMyIpCreationOrigin();
  const toLogin = useOfficialStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const sourceKey = JSON.stringify([requireCanvas, origin === undefined ? { kind: 'new-project' } : origin, session.paths.prefix]);
  const sourceAvailable = origin !== null && (!requireCanvas || origin?.kind === 'canvas');
  const requestRef = useRef<CreationRequest>();
  const refreshRequired = useRef<string>();
  const mounted = useRef(false);
  const current = useRef({ action, session, sourceKey });
  current.current = { action, session, sourceKey };
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);

  /** 取消只消除意图；真实创建失败继续交给入口显示既有错误文案。 */
  const finish = useCallback((request: CreationRequest, error?: unknown) => {
    if (requestRef.current !== request) return;
    requestRef.current = undefined;
    if (mounted.current) { setPending(false); setFailed(error !== undefined); }
    if (error !== undefined) request.reject(error);
    else request.resolve();
  }, []);

  /** 登录弹窗关闭、宿主卸载或切换来源后，不再初始化或续接旧创建。 */
  useEffect(() => {
    mounted.current = true;
    /** 刷新期间也同步撤销切账号意图，旧刷新后来写回原 UID 不能恢复创建。 */
    const unsubscribe = useOfficialStore.subscribe(() => {
      const identity = getCurrentIdentity();
      if (refreshRequired.current && identity.uid !== refreshRequired.current) refreshRequired.current = undefined;
      const request = requestRef.current;
      if (!request?.uid) return;
      if (identity.uid !== request.uid || (request.scopeKey && identity.scopeKey !== request.scopeKey)) finish(request);
    });
    return () => {
      mounted.current = false;
      refreshRequired.current = undefined;
      unsubscribe();
      if (requestRef.current) finish(requestRef.current);
    };
  }, [finish]);

  /** 身份提交完成且来源画布真正载入后，才用最新创建函数执行一次。 */
  useEffect(() => {
    const request = requestRef.current;
    if (!request) return;
    if (request.sourceKey !== sourceKey) { finish(request); return; }
    const identity = getCurrentIdentity();
    if (request.uid && identity.uid !== request.uid) { finish(request); return; }
    if (request.phase !== 'waiting') return;
    if (!identity.uid || identity.uid !== request.uid || identity.scopeKey !== request.scopeKey) { finish(request); return; }
    if (!session.ready || session.scopeKey !== request.scopeKey) return;
    if (!session.enabled) { finish(request, new Error('IP login initialization failed')); return; }
    if (!action.enabled) return;
    request.phase = 'creating';
    action.create().then(() => finish(request), (error: unknown) => finish(request, error));
  }, [action, session.ready, session.enabled, session.scopeKey, sourceKey, revision, finish]);

  /** 重复点击共用 Promise；登录取消不报创建失败，刷新失败不留下未来自动创建的意图。 */
  const create = (): Promise<void> => {
    if (requestRef.current) return requestRef.current.promise;
    const identity = getCurrentIdentity();
    const needsRefresh = refreshRequired.current === identity.uid && Boolean(identity.uid);
    if (!mounted.current || !session.ready || !sourceAvailable || (session.enabled && !action.enabled && !needsRefresh)) return Promise.resolve();
    let resolve!: () => void;
    let reject!: (error: unknown) => void;
    const promise = new Promise<void>((done, fail) => { resolve = done; reject = fail; });
    const request: CreationRequest = {
      sourceKey,
      phase: session.enabled && !needsRefresh ? 'creating' : 'login',
      promise,
      resolve,
      reject,
      ...(session.enabled && !needsRefresh ? identity : {}),
    };
    requestRef.current = request;
    setPending(true);
    setFailed(false);
    /** 每个异步边界核对稳定宿主，禁止将登录结果交给后来打开的其他来源。 */
    const isCurrent = () => mounted.current && requestRef.current === request && current.current.sourceKey === request.sourceKey;
    if (session.enabled && !needsRefresh) {
      action.create().then(() => finish(request), (error: unknown) => finish(request, error));
    } else {
      (async () => {
        if (!session.enabled) {
          try { await toLogin(); } catch { finish(request); return; }
        }
        if (!isCurrent()) return;
        const loggedIn = getCurrentIdentity();
        if (!loggedIn.uid) throw new Error('IP login initialization failed');
        request.uid = loggedIn.uid;
        refreshRequired.current = loggedIn.uid;
        request.phase = 'refresh';
        await initUserInfo({ throwOnError: true });
        if (!isCurrent()) return;
        const refreshed = getCurrentIdentity();
        if (!refreshed.uid || refreshed.uid !== request.uid) throw new Error('IP login identity changed');
        refreshRequired.current = undefined;
        request.scopeKey = refreshed.scopeKey;
        request.phase = 'waiting';
        setRevision((value) => value + 1);
      })().catch((error: unknown) => { if (isCurrent()) finish(request, error); });
    }
    return promise;
  };

  return {
    create,
    failed,
    pending,
    disabled: !session.ready || !sourceAvailable || pending || action.pending
      || (session.enabled && !action.enabled && !refreshRequired.current),
  };
}
